// Ultra-Optimized Main JavaScript - Performance-focused
(function() {
  'use strict';

  // Early return if already initialized
  if (window.EzeeNotesTheme && window.EzeeNotesTheme.initialized) return;

  // Polyfills
  window.requestIdleCallback = window.requestIdleCallback || function(cb) { return setTimeout(cb, 1); };
  window.requestAnimationFrame = window.requestAnimationFrame || function(cb) { return setTimeout(cb, 16); };

  // Optimized DOM cache with WeakMap for memory efficiency
  const cache = new Map();
  const q = (s) => cache.get(s) || (cache.set(s, document.querySelector(s)), cache.get(s));
  const qa = (s) => cache.get(s + '_all') || (cache.set(s + '_all', document.querySelectorAll(s)), cache.get(s + '_all'));

  // Performance monitoring
  const perf = {
    start: performance.now(),
    mark: (name) => performance.mark && performance.mark(name),
    measure: (name, start) => performance.measure && performance.measure(name, start)
  };

  class EzeeNotesTheme {
    constructor() {
      if (EzeeNotesTheme.instance) return EzeeNotesTheme.instance;
      EzeeNotesTheme.instance = this;

      this.initialized = false;
      this.cartCache = new Map();
      this.cartCacheTime = 30000;
      this.debounceTimers = new Map();
      this.abortController = new AbortController();

      perf.mark('theme-start');
      this.init();
    }

    init() {
      if (this.initialized) return;
      this.initialized = true;

      perf.mark('init-start');

      // Critical features first
      this.initCartFunctionality();
      this.initCategoryFilter(); // Add category filter initialization

      // Use requestIdleCallback for non-critical features
      requestIdleCallback(() => {
        this.initProductCardButtons();
        this.initProductGallery();
        this.initQuantitySelectors();
      }, { timeout: 2000 });

      // Defer least critical features
      requestIdleCallback(() => {
        this.initAccordions();
        this.initBuyNowButtons();
      }, { timeout: 5000 });

      perf.mark('init-end');
      perf.measure('init-duration', 'init-start', 'init-end');
    }

    // ======================================
    // Collection Showcase & Category Filter
    // ======================================
    initCategoryFilter() {
      const showcaseWrapper = q('#collection-showcase');
      if (!showcaseWrapper) return;

      this.currentCategory = 'all'; // Default - show all products
      this.categorySections = qa('.category-section');
      this.collectionCards = qa('.collection-image-card');

      // Explicitly remove all active classes on init
      this.collectionCards.forEach(card => {
        card.classList.remove('active');
        card.setAttribute('aria-pressed', 'false');
      });

      // Read initial state from URL hash
      const hash = window.location.hash.replace('#', '');
      if (hash && hash.startsWith('category-')) {
        this.currentCategory = hash.replace('category-', '');
      }

      // Apply initial filter
      this.filterByCategory(this.currentCategory, false);

      // Event delegation for collection card clicks
      showcaseWrapper.addEventListener('click', (e) => {
        const card = e.target.closest('.collection-image-card');
        if (card) {
          // Toggle: if clicking active card, show all; otherwise show that category
          if (card.classList.contains('active')) {
            // Deselect - show all products
            this.filterByCategory('all', true);
            window.history.replaceState(null, '', window.location.pathname);
          } else {
            // Select this category
            const category = card.dataset.category;
            this.filterByCategory(category, true);
            window.history.replaceState(null, '', `#category-${category}`);

            // Track analytics event
            if (typeof gtag !== 'undefined') {
              gtag('event', 'collection_card_click', {
                'event_category': 'Collection Navigation',
                'event_label': category
              });
            }
          }

          // Smooth scroll to products
          const productsWrapper = q('.products-wrapper');
          if (productsWrapper) {
            setTimeout(() => {
              productsWrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 300);
          }
        }
      }, { passive: true });

      // Keyboard navigation for collection cards
      this.collectionCards.forEach(card => {
        card.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            card.click();
          }
        });
      });

      // Handle browser back/forward
      window.addEventListener('hashchange', () => {
        const hash = window.location.hash.replace('#', '');
        if (hash && hash.startsWith('category-')) {
          const category = hash.replace('category-', '');
          this.filterByCategory(category, true);
        }
      }, { passive: true });
    }

    filterByCategory(category, animated = true) {
      this.currentCategory = category;
      let visibleCount = 0;

      // Update active collection card
      this.collectionCards.forEach(card => {
        // When category is 'all', no card should be active
        if (category !== 'all' && card.dataset.category === category) {
          card.classList.add('active');
          card.setAttribute('aria-pressed', 'true');
        } else {
          card.classList.remove('active');
          card.setAttribute('aria-pressed', 'false');
        }
      });

      // Filter sections with animation
      this.categorySections.forEach((section, index) => {
        const sectionCategory = section.dataset.category;
        const shouldShow = category === 'all' || sectionCategory === category;

        if (shouldShow) {
          // Count products in this section
          const productCards = section.querySelectorAll('.product-card');
          visibleCount += productCards.length;

          // Show with animation
          if (animated) {
            section.style.display = 'block';
            section.style.opacity = '0';
            section.style.transform = 'translateY(20px)';

            // Stagger animation
            requestAnimationFrame(() => {
              setTimeout(() => {
                section.style.transition = 'opacity 0.4s ease, transform 0.4s ease';
                section.style.opacity = '1';
                section.style.transform = 'translateY(0)';
              }, index * 80); // Stagger by 80ms
            });
          } else {
            section.style.display = 'block';
            section.style.opacity = '1';
            section.style.transform = 'translateY(0)';
          }
        } else {
          // Hide
          if (animated) {
            section.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
            section.style.opacity = '0';
            section.style.transform = 'translateY(-10px)';
            setTimeout(() => {
              section.style.display = 'none';
            }, 300);
          } else {
            section.style.display = 'none';
          }
        }
      });

      // Update product count and collection name
      this.updateVisibleCount(visibleCount);
      this.updateActiveCollectionName(category);

      // Lazy load images in visible sections
      this.lazyLoadVisibleImages();
    }

    updateVisibleCount(count) {
      const countEl = q('#visible-product-count');
      if (countEl) {
        countEl.textContent = count;
      }
    }

    updateActiveCollectionName(category) {
      // No longer displaying collection name - removed
    }

    lazyLoadVisibleImages() {
      // Load images in visible sections only
      const visibleSections = qa('.category-section[style*="display: block"], .category-section:not([style*="display"])');

      visibleSections.forEach(section => {
        const lazyImages = section.querySelectorAll('img[loading="lazy"]');
        lazyImages.forEach(img => {
          // Check if in viewport
          const rect = img.getBoundingClientRect();
          const inViewport = (
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) + 500 &&
            rect.right <= (window.innerWidth || document.documentElement.clientWidth)
          );

          if (inViewport && img.dataset.src) {
            img.src = img.dataset.src;
            delete img.dataset.src;
          }
        });
      });
    }

    // Optimized gallery with event delegation
    initProductGallery() {
      // Logic handled inline in product-details.liquid to support srcset and high-res images
    }

    // Optimized accordions with single listener
    initAccordions() {
      const container = q('.accordion-container') || document.body;
      container.addEventListener('click', (e) => {
        const btn = e.target.closest('.accordion-button');
        if (!btn) return;
        const content = btn.nextElementSibling;
        const icon = btn.querySelector('.accordion-icon');
        if (!content || !icon) return;
        const isOpen = content.classList.contains('open');
        // Close others
        qa('.accordion-content.open').forEach(c => {
          if (c !== content) {
            c.classList.remove('open');
            c.classList.add('closed');
            const otherIcon = c.previousElementSibling?.querySelector('.accordion-icon');
            if (otherIcon) otherIcon.classList.remove('rotated');
          }
        });
        // Toggle current
        content.classList.toggle('open', !isOpen);
        content.classList.toggle('closed', isOpen);
        icon.classList.toggle('rotated', !isOpen);
      }, { passive: true });
    }

    // Optimized quantity selectors
    initQuantitySelectors() {
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('.quantity__button, .quantity-minus, .quantity-plus');
        if (!btn) return;
        const selector = btn.closest('.quantity-selector');
        const input = selector?.querySelector('.quantity-input, .quantity__input');
        if (!input) return;
        const val = parseInt(input.value) || 1;
        const isMinus = btn.classList.contains('quantity-minus') || btn.classList.contains('quantity__button') && btn.querySelector('path[d*="M5 12h14"]');
        input.value = isMinus ? Math.max(1, val - 1) : val + 1;
        // Debounce update
        const timerId = 'quantity_' + Math.random();
        this.debounceTimers.forEach((timer, id) => clearTimeout(timer));
        this.debounceTimers.set(timerId, setTimeout(() => {
          this.updateLineItem(input);
          this.debounceTimers.delete(timerId);
        }, 300));
      });
    }

    // Fixed cart functionality
    initCartFunctionality() {
      // Event delegation for all cart buttons - handle both product grid and product details
      document.addEventListener('click', async (e) => {
        // Prevent multiple rapid clicks
        const target = e.target.closest('.add-to-cart-btn, .buy-now-button, .product-add-to-cart-btn, .product-remove-from-cart-btn');
        if (target && target.classList.contains('processing')) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        // Handle product detail page add to cart
        const addBtn = e.target.closest('.add-to-cart-btn');
        if (addBtn && !addBtn.classList.contains('btn-buy-now')) {
          e.preventDefault();
          e.stopPropagation();
          await this.addToCart(addBtn);
          return;
        }
        // Handle product grid add to cart
        const productAddBtn = e.target.closest('.product-add-to-cart-btn');
        if (productAddBtn) {
          e.preventDefault();
          e.stopPropagation();
          const controls = productAddBtn.closest('.product-cart-controls');
          const variantId = controls?.dataset.variantId;
          if (variantId && !productAddBtn.classList.contains('processing')) {
            await this.handleProductButton(productAddBtn, variantId, true);
          }
          return;
        }
        // Handle product grid remove from cart
        const productRemoveBtn = e.target.closest('.product-remove-from-cart-btn');
        if (productRemoveBtn) {
          e.preventDefault();
          e.stopPropagation();
          const controls = productRemoveBtn.closest('.product-cart-controls');
          const variantId = controls?.dataset.variantId;
          if (variantId && !productRemoveBtn.classList.contains('processing')) {
            await this.handleProductButton(productRemoveBtn, variantId, false);
          }
          return;
        }
      }, { passive: false });
    }

    // Optimized buy now buttons
    initBuyNowButtons() {
      document.addEventListener('click', async (e) => {
        const buyBtn = e.target.closest('.btn-buy-now');
        if (buyBtn && !buyBtn.classList.contains('processing')) {
          e.preventDefault();
          await this.buyNow(buyBtn);
        }
      }, { passive: false });
    }

    // ✅ OPTIMIZED PRODUCT CARD BUTTONS - FETCHES CART ONLY ONCE
    async initProductCardButtons() {
        const controls = qa('.product-cart-controls');
        if (controls.length === 0) return;

        try {
            // 1. Fetch cart data ONCE
            const response = await fetch('/cart.js');
            const cart = await response.json();
            const cartVariantIds = new Set(cart.items.map(item => item.variant_id));

            // 2. Iterate through controls and update UI from stored data
            controls.forEach(control => {
                const variantId = parseInt(control.dataset.variantId, 10);
                if (cartVariantIds.has(variantId)) {
                    control.querySelector('.product-add-to-cart-btn')?.classList.add('hidden');
                    control.querySelector('.product-remove-from-cart-btn')?.classList.remove('hidden');
                }
            });
        } catch (error) {
            console.error('Failed to initialize product card buttons:', error);
        }
    }

    // Unified product button handler
    async handleProductButton(btn, variantId, isAdd) {
      if (btn.classList.contains('processing')) return;
      btn.classList.add('processing');
      const btnText = btn.querySelector('.btn-text');
      const originalText = btnText?.textContent || '';
      if (btnText) btnText.textContent = isAdd ? 'Adding...' : 'Removing...';
      try {
        const requestData = isAdd
        ? { id: variantId, quantity: 1 }
        : { id: variantId, quantity: 0 };
        const response = await fetch(isAdd ? '/cart/add.js' : '/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(requestData)
        });
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Cart API response not ok:', response.status, errorText);
          throw new Error(`Server error: ${response.status}`);
        }
        const data = await response.json();
        this.updateCartCount(data.item_count);
        this.clearCartCache();
        btn.classList.add(isAdd ? 'added' : 'removed');
        if (btnText) btnText.textContent = isAdd ? 'Added!' : 'Removed!';
        // Toggle buttons
        const controls = btn.closest('.product-cart-controls');
        const addBtn = controls?.querySelector('.product-add-to-cart-btn');
        const removeBtn = controls?.querySelector('.product-remove-from-cart-btn');
        if (isAdd) {
          if (addBtn) addBtn.classList.add('hidden');
          if (removeBtn) removeBtn.classList.remove('hidden');
        } else {
          if (removeBtn) removeBtn.classList.add('hidden');
          if (addBtn) addBtn.classList.remove('hidden');
        }
        this.showNotification('success', isAdd ? 'Added to cart!' : 'Removed from cart!');
        setTimeout(() => {
          btn.classList.remove('processing', isAdd ? 'added' : 'removed');
          if (btnText) btnText.textContent = originalText;
        }, 2000);
      } catch (error) {
        console.error('Cart error:', error);
        if (btnText) btnText.textContent = 'Failed';
        this.showNotification('error', 'Failed to update cart. Please try again.');
        setTimeout(() => {
          if (btnText) btnText.textContent = originalText;
          btn.classList.remove('processing');
        }, 2000);
      }
    }

    // ✅ DELETED old checkIfInCart function, as it is no longer needed.

    clearCartCache() {
      this.cartCache.clear();
    }
    // Fixed add to cart for product details
    async addToCart(btn) {
      if (btn.classList.contains('processing')) return;
      const form = btn.closest('form, .product-form-wrapper, product-form, .product-form');
      const variantInput = form?.querySelector('[name="id"]');
      const quantityInput = form?.querySelector('.quantity-input, .quantity__input, [name="quantity"]');
      const variantId = variantInput?.value;
      const quantity = quantityInput?.value || 1;
      if (!variantId) {
        console.error('No variant ID found');
        this.showNotification('error', 'Product variant not found');
        return;
      }
      btn.classList.add('processing');
      const buttonText = btn.querySelector('span') || btn;
      const originalText = buttonText.textContent;
      buttonText.textContent = 'Adding...';
      // Show loading spinner if it exists
      const spinner = form?.querySelector('.loading-overlay__spinner');
      if (spinner) spinner.classList.remove('hidden');
      try {
        const requestData = {
          id: variantId,
          quantity: parseInt(quantity)
        };
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(requestData)
        });
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Add to cart failed:', response.status, errorText);
          throw new Error(`Failed to add: ${response.status} - ${errorText}`);
        }
        const result = await response.json();
        this.updateCartCount(result.item_count);
        this.clearCartCache();
        this.showNotification('success', 'Added to cart!');
        buttonText.textContent = 'Added!';
        setTimeout(() => {
          buttonText.textContent = originalText;
        }, 2000);
      } catch (error) {
        console.error('Cart error:', error);
        this.showNotification('error', 'Failed to add to cart. Please try again.');
        buttonText.textContent = 'Failed';
        setTimeout(() => {
          buttonText.textContent = originalText;
        }, 2000);
      } finally {
        btn.classList.remove('processing');
        if (spinner) spinner.classList.add('hidden');
      }
    }
    // Optimized buy now
    async buyNow(btn) {
      if (btn.classList.contains('processing')) return;
      const form = btn.closest('form, .product-form-wrapper, product-form, .product-form');
      const variantId = form?.querySelector('[name="id"]')?.value;
      const quantity = form?.querySelector('.quantity-input, .quantity__input, [name="quantity"]')?.value || 1;
      if (!variantId) return;
      btn.classList.add('processing');
      btn.disabled = true;
      const buttonText = btn.querySelector('span') || btn;
      const originalText = buttonText.textContent;
      buttonText.textContent = 'Processing...';
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ id: variantId, quantity: parseInt(quantity) })
        });
        if (response.ok) {
          window.location.href = '/checkout';
        } else {
          throw new Error('Failed to process');
        }
      } catch (error) {
        console.error('Buy now error:', error);
        this.showNotification('error', 'Failed to process order');
        buttonText.textContent = originalText;
        btn.classList.remove('processing');
        btn.disabled = false;
      }
    }
    // Optimized line item update
    async updateLineItem(input) {
      const key = input.dataset.lineItemKey;
      if (!key) return;
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ id: key, quantity: parseInt(input.value) })
        });
        if (response.ok) {
          const cart = await response.json();
          this.updateCartCount(cart.item_count);
          this.clearCartCache();
        }
      } catch (error) {
        console.error('Update error:', error);
      }
    }
    // Optimized cart count update
    updateCartCount(count) {
      const el = q('#cart-count');
      if (el) el.textContent = count;
      if (typeof window.cartCount !== 'undefined') {
        window.cartCount = count;
      }
    }
    // Unified notification system
    showNotification(type, message) {
      // Remove existing
      const existing = q('.cart-notification');
      if (existing) existing.remove();
      const notif = document.createElement('div');
      notif.className = 'cart-notification';
      notif.innerHTML = `
        <div class="cart-notification-content">
          <span>${message}</span>
          ${type === 'success' ? '<a href="/cart" style="margin-left: 10px; text-decoration: underline;">View Cart</a>' : ''}
        </div>
      `;
      Object.assign(notif.style, {
        position: 'fixed',
        top: '100px',
        right: '20px',
        zIndex: '1000',
        background: type === 'success' ? 'white' : '#fee2e2',
        color: type === 'success' ? '#065f46' : '#dc2626',
        borderRadius: '8px',
        padding: '1rem',
        boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
        border: `1px solid ${type === 'success' ? '#d1fae5' : '#fecaca'}`,
        transform: 'translateX(100%)',
        transition: 'transform 0.3s ease',
        maxWidth: '300px'
      });
      document.body.appendChild(notif);
      requestAnimationFrame(() => {
        notif.style.transform = 'translateX(0)';
      });
      setTimeout(() => {
        notif.style.transform = 'translateX(100%)';
        setTimeout(() => notif.remove(), 300);
      }, 4000);
    }
  }
  // Mobile Menu Class
  class MobileMenu {
    constructor() {
      this.hamburger = document.querySelector('.hamburger-menu');
      this.menu = document.querySelector('.mobile-menu');
      this.overlay = document.querySelector('.mobile-menu-overlay');
      this.closeBtn = document.querySelector('.mobile-menu-close');

      this.init();
    }

    init() {
      if (!this.hamburger || !this.menu) return;

      // Open/close menu
      this.hamburger.addEventListener('click', () => this.toggleMenu());

      // Close menu
      this.overlay?.addEventListener('click', () => this.closeMenu());
      this.closeBtn?.addEventListener('click', () => this.closeMenu());

      // Close on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.menu.classList.contains('active')) {
          this.closeMenu();
        }
      });

      // Close menu when clicking on a link
      const menuLinks = this.menu.querySelectorAll('.mobile-menu-link');
      menuLinks.forEach(link => {
        link.addEventListener('click', () => {
          // Allow navigation, then close menu
          setTimeout(() => this.closeMenu(), 100);
        });
      });
    }

    toggleMenu() {
      const isOpen = this.menu.classList.contains('active');
      if (isOpen) {
        this.closeMenu();
      } else {
        this.openMenu();
      }
    }

    openMenu() {
      this.menu.classList.add('active');
      this.overlay.classList.add('active');
      this.hamburger.classList.add('active');
      this.hamburger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('mobile-menu-open');
    }

    closeMenu() {
      this.menu.classList.remove('active');
      this.overlay.classList.remove('active');
      this.hamburger.classList.remove('active');
      this.hamburger.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('mobile-menu-open');
    }
  }

  // Initialize when ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new EzeeNotesTheme();
      new MobileMenu();
    }, { once: true });
  } else {
    new EzeeNotesTheme();
    new MobileMenu();
  }
  // Expose utilities
  window.EzeeNotesTheme = EzeeNotesTheme;
  window.MobileMenu = MobileMenu;
})();